import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  Phone,
  ExternalLink,
  Clock,
  AlertTriangle,
  Briefcase,
  DoorOpen,
  Home as HomeIcon,
  Lightbulb,
  ArrowRight,
  MapPin,
} from 'lucide-react';
import {
  DA_TYPES,
  DA_LEGAL,
  DA_SAFETY_PLAN,
  DA_ORGS,
  DA_PROCESS,
  DA_RIGHTS,
} from '../data/content';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';
import HelplinesStrip from '../components/HelplinesStrip';
import NationPicker from '../components/NationPicker';
import { IfEnglandWales, IfScotland } from '../i18n/NationContext';

function Collapsible({ id, title, body, testid, icon: Icon }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      data-testid={testid || `da-collapsible-${id}`}
      className="tf-card text-left p-5"
      style={{ cursor: 'pointer' }}
    >
      <div className="flex items-center gap-3">
        {Icon && (
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--tf-purple-light)', color: 'var(--tf-purple-dark)' }}
          >
            <Icon size={18} />
          </div>
        )}
        <h3
          className="font-heading flex-1"
          style={{ fontSize: 17, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          {title}
        </h3>
        {open ? (
          <ChevronUp size={18} color="var(--tf-text-muted)" />
        ) : (
          <ChevronDown size={18} color="var(--tf-text-muted)" />
        )}
      </div>
      <div
        className="mt-4 pt-4 tf-prose print-show"
        data-print-expand="true"
        style={{
          borderTop: '1px solid var(--tf-card-border)',
          display: open ? 'block' : 'none',
        }}
      >
        <p style={{ fontSize: 14, lineHeight: 1.7 }}>{body}</p>
      </div>
    </button>
  );
}

function SafetyBlock({ title, items, icon: Icon, testid }) {
  return (
    <div className="tf-card p-5" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'var(--tf-bg-secondary)', color: 'var(--tf-purple)' }}
        >
          <Icon size={18} />
        </div>
        <h3 className="font-heading" style={{ fontSize: 17, color: 'var(--tf-text)' }}>
          {title}
        </h3>
      </div>
      <ul className="flex flex-col gap-2.5 mt-2">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2.5">
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: 2.5,
                background: 'var(--tf-purple)',
                marginTop: 9,
                flexShrink: 0,
              }}
            />
            <p
              className="font-body"
              style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--tf-text-secondary)' }}
            >
              {item}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DAProcessStage({ stage, idx, isLast }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="relative pl-12" data-testid={`da-stage-${stage.id}`}>
      {!isLast && (
        <span
          aria-hidden
          className="absolute"
          style={{
            left: 18,
            top: 36,
            bottom: -10,
            width: 2,
            background:
              'linear-gradient(180deg, var(--tf-purple-light) 0%, transparent 100%)',
          }}
        />
      )}
      <div
        className="absolute flex items-center justify-center"
        style={{
          left: 0,
          top: 0,
          width: 38,
          height: 38,
          borderRadius: 19,
          background: '#fff',
          border: `2px solid var(--tf-purple)`,
          color: 'var(--tf-purple)',
          fontFamily: 'Fraunces, serif',
          fontWeight: 600,
          fontSize: 15,
        }}
      >
        {idx + 1}
      </div>

      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`da-stage-toggle-${stage.id}`}
        className="w-full text-left tf-card p-5"
        style={{ marginBottom: 16, cursor: 'pointer' }}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h3
              className="font-heading"
              style={{ fontSize: 19, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
            >
              {stage.title}
            </h3>
            <p
              className="font-body mt-1"
              style={{
                fontSize: 12.5,
                color: 'var(--tf-purple)',
                fontWeight: 700,
                letterSpacing: '0.02em',
              }}
            >
              {stage.subtitle}
            </p>
          </div>
          {open ? (
            <ChevronUp size={20} color="var(--tf-text-muted)" />
          ) : (
            <ChevronDown size={20} color="var(--tf-text-muted)" />
          )}
        </div>

        <div
          className="mt-4 pt-4 print-show"
          data-print-expand="true"
          style={{
            borderTop: '1px solid var(--tf-card-border)',
            display: open ? 'block' : 'none',
          }}
        >
          <div className="tf-prose">
            {stage.body.split('\n\n').map((para, i) => (
              <p key={i} style={{ fontSize: 14, lineHeight: 1.7 }}>
                {para}
              </p>
            ))}
          </div>
          {stage.tips && stage.tips.length > 0 && (
            <div className="mt-4 p-4 rounded-xl" style={{ background: 'var(--tf-pink-soft)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb size={15} color="var(--tf-purple-dark)" />
                <p
                  className="font-body font-bold"
                  style={{
                    fontSize: 11.5,
                    color: 'var(--tf-purple-dark)',
                    letterSpacing: '0.1em',
                  }}
                >
                  GOOD TO KNOW
                </p>
              </div>
              <ul className="flex flex-col gap-2">
                {stage.tips.map((t, i) => (
                  <li key={i} className="flex gap-2">
                    <span
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: 2.5,
                        background: 'var(--tf-purple)',
                        marginTop: 9,
                        flexShrink: 0,
                      }}
                    />
                    <p
                      className="font-body"
                      style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--tf-text)' }}
                    >
                      {t}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </button>
    </div>
  );
}

export default function DomesticAbuse() {
  // Scroll to hash anchor if present (e.g. /app/domestic-abuse#process)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash?.replace('#', '');
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="da-screen">
      <SEO
        title="Domestic Abuse — UK Guide for Survivors"
        description="Coercive control, financial abuse, 'honour'-based abuse — the seven UK-recognised forms, your legal protections (Domestic Abuse Act 2021), safety planning, DA judicial process and specialist UK helplines for women, men, non-binary and LGBT+ survivors (Refuge, Women's Aid, Mankind Initiative, Men's Advice Line, NCDV, Galop)."
        path="/app/domestic-abuse"
      />
      <p className="eyebrow">DOMESTIC ABUSE — YOUR CALM GUIDE</p>
      <h1
        className="font-heading mt-3"
        style={{
          fontSize: 'clamp(28px, 4vw, 36px)',
          lineHeight: 1.15,
          color: 'var(--tf-text)',
          letterSpacing: '-0.01em',
        }}
      >
        It isn&apos;t just bruises.<br />
        It&apos;s a pattern.
      </h1>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 14.5, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        Domestic abuse takes many forms — physical, sexual, emotional, financial, coercive,
        digital. Choose your nation below so we can show you the right law, agencies and
        specialist support. You have the right to safety, to specialist support, and to
        leave — at the time and pace that is safest for you.
      </p>

      <NationPicker testid="da-nation-picker" />

      <div className="mt-6 flex gap-2 flex-wrap">
        <PrintButton label="Print / Save this page" />
      </div>

      {/* Reassuring trauma-informed message */}
      <section
        className="mt-6 tf-card p-5 sm:p-6"
        data-testid="da-reassurance"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #FBEFF2 100%)',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background:
                'linear-gradient(135deg, var(--tf-pink) 0%, var(--tf-purple) 100%)',
              color: '#fff',
              boxShadow: '0 8px 22px rgba(217,169,180,0.32)',
            }}
          >
            <ShieldCheck size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="font-body font-bold"
              style={{
                fontSize: 10.5,
                letterSpacing: '0.22em',
                color: 'var(--tf-purple)',
              }}
            >
              BEFORE YOU READ ON
            </p>
            <h2
              className="font-heading mt-2"
              style={{
                fontSize: 'clamp(20px, 2.4vw, 23px)',
                color: 'var(--tf-text)',
                letterSpacing: '-0.01em',
                lineHeight: 1.25,
              }}
            >
              You are not overreacting. A pattern is abuse — even without a single bruise.
            </h2>
            <p
              className="font-body mt-3"
              style={{
                fontSize: 13.5,
                color: 'var(--tf-text-secondary)',
                lineHeight: 1.7,
              }}
            >
              Coercive control, financial control, isolation, gaslighting, jealousy that
              calls itself love — these are abuse under UK law. You do not need physical
              injuries to deserve support, or to be believed.
            </p>
            <p
              className="font-body mt-2"
              style={{
                fontSize: 13.5,
                color: 'var(--tf-text-secondary)',
                lineHeight: 1.7,
              }}
            >
              You can read a little, leave, and come back. You can stay, plan, and leave
              when it is safest. <strong style={{ color: 'var(--tf-purple-dark)' }}>You set the pace.</strong>
            </p>
            <p
              className="font-body mt-4 inline-flex items-center gap-2"
              style={{
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: '0.16em',
                color: 'var(--tf-purple-dark)',
              }}
            >
              YOU ARE BELIEVED · YOU ARE NOT ALONE
            </p>
          </div>
        </div>
      </section>

      {/* Immediate-help banner */}
      <div
        className="mt-6 rounded-2xl p-5 flex items-start gap-4"
        style={{ background: 'var(--tf-purple)' }}
        data-testid="da-crisis-banner"
      >
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(255,255,255,0.2)' }}
        >
          <AlertTriangle size={20} color="#fff" />
        </div>
        <div className="flex-1">
          <p className="font-body font-bold" style={{ fontSize: 14, color: '#fff' }}>
            In immediate danger?
          </p>
          <p
            className="font-body"
            style={{ fontSize: 13, color: 'rgba(255,255,255,0.95)', marginTop: 4, lineHeight: 1.55 }}
          >
            Call <a href="tel:999" className="underline" style={{ color: '#fff' }}>999</a>. If you
            can&apos;t speak, dial 999 then press <strong>55</strong> (Silent Solution) — the
            operator will know you need help. Or call the 24/7 National Domestic Abuse Helpline:{' '}
            <a href="tel:08082000247" className="underline" style={{ color: '#fff' }}>
              0808 2000 247
            </a>
            .
          </p>
        </div>
      </div>

      <HelplinesStrip variant="da" />

      {/* "Is this abuse?" doorway to the "crossed a line" guide */}
      <Link
        to="/app/crossed-the-line"
        data-testid="da-is-this-abuse"
        className="mt-6 block tf-card tf-card-hover p-5"
        style={{
          textDecoration: 'none',
          color: 'var(--tf-text)',
          background: '#FFFDFA',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p
              className="font-body font-bold"
              style={{ fontSize: 10.5, letterSpacing: '0.22em', color: 'var(--tf-purple-dark)' }}
            >
              IF YOU&apos;RE DOUBTING YOURSELF
            </p>
            <h3
              className="font-heading mt-1"
              style={{
                fontSize: 20,
                color: 'var(--tf-text)',
                letterSpacing: '-0.01em',
                lineHeight: 1.25,
              }}
            >
              Is this abuse?
            </h3>
            <p
              className="font-body mt-2"
              style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
            >
              A gentle guide to recognising coercive control, emotional harm, financial
              abuse and digital monitoring — with quiet answers to the thoughts that
              keep survivors doubting themselves.
            </p>
          </div>
          <ArrowRight size={18} color="var(--tf-purple-dark)" style={{ flexShrink: 0, marginTop: 6 }} />
        </div>
      </Link>

      <IfEnglandWales>

      {/* Types of abuse */}
      <div id="types" className="mt-10 mb-4" style={{ scrollMarginTop: 96 }}>
        <p className="eyebrow">WHAT DOMESTIC ABUSE LOOKS LIKE</p>
        <h2 className="font-heading mt-2" style={{ fontSize: 22, color: 'var(--tf-text)' }}>
          The seven forms recognised in UK law
        </h2>
        <p
          className="font-body mt-2"
          style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
        >
          The Domestic Abuse Act 2021 defines abuse broadly — not just physical. You may recognise
          one of these, several, or all of them.
        </p>
      </div>
      <div className="flex flex-col gap-3">
        {DA_TYPES.map((t) => (
          <Collapsible
            key={t.id}
            id={t.id}
            title={t.title}
            body={t.body}
            testid={`da-type-${t.id}`}
            icon={ShieldCheck}
          />
        ))}
      </div>

      {/* Legal protections */}
      <div id="legal" className="mt-12 mb-4" style={{ scrollMarginTop: 96 }}>
        <p className="eyebrow">YOUR LEGAL PROTECTIONS</p>
        <h2 className="font-heading mt-2" style={{ fontSize: 22, color: 'var(--tf-text)' }}>
          UK laws on your side
        </h2>
        <p
          className="font-body mt-2"
          style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
        >
          Tap any law or order to read more. Legal aid is normally available to domestic abuse
          survivors, regardless of income.
        </p>
      </div>
      <div className="flex flex-col gap-3">
        {DA_LEGAL.map((l) => (
          <Collapsible
            key={l.id}
            id={l.id}
            title={l.title}
            body={l.body}
            testid={`da-law-${l.id}`}
            icon={ShieldCheck}
          />
        ))}
      </div>

      {/* Safety plan */}
      <div id="safety" className="mt-12 mb-4" style={{ scrollMarginTop: 96 }}>
        <p className="eyebrow">SAFETY PLANNING</p>
        <h2 className="font-heading mt-2" style={{ fontSize: 22, color: 'var(--tf-text)' }}>
          A safety plan, just for you
        </h2>
        <p
          className="font-body mt-2"
          style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
        >
          {DA_SAFETY_PLAN.intro}
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <SafetyBlock
          title="While still living together"
          items={DA_SAFETY_PLAN.whileLiving}
          icon={HomeIcon}
          testid="safety-while-living"
        />
        <SafetyBlock
          title="What to put in a 'go bag'"
          items={DA_SAFETY_PLAN.goBag}
          icon={Briefcase}
          testid="safety-go-bag"
        />
        <SafetyBlock
          title="Leaving safely"
          items={DA_SAFETY_PLAN.leaving}
          icon={DoorOpen}
          testid="safety-leaving"
        />
        <SafetyBlock
          title="After you have left"
          items={DA_SAFETY_PLAN.afterLeaving}
          icon={Lightbulb}
          testid="safety-after"
        />
      </div>

      {/* Judicial process for DA */}
      <div id="process" className="mt-12 mb-4" style={{ scrollMarginTop: 96 }}>
        <p className="eyebrow">THE JUDICIAL PROCESS FOR DOMESTIC ABUSE</p>
        <h2 className="font-heading mt-2" style={{ fontSize: 22, color: 'var(--tf-text)' }}>
          From recognising abuse to after the case
        </h2>
        <p
          className="font-body mt-2"
          style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
        >
          A trauma-informed walkthrough of how a domestic-abuse case can move through the UK
          system — including the steps you can take before any police involvement. Tap any stage to
          open it.
        </p>
      </div>
      <div data-testid="da-process">
        {DA_PROCESS.map((stage, i) => (
          <DAProcessStage
            key={stage.id}
            stage={stage}
            idx={i}
            isLast={i === DA_PROCESS.length - 1}
          />
        ))}
      </div>

      {/* Rights for DA */}
      <div id="rights" className="mt-12 mb-4" style={{ scrollMarginTop: 96 }}>
        <p className="eyebrow">YOUR RIGHTS UNDER UK LAW</p>
        <h2 className="font-heading mt-2" style={{ fontSize: 22, color: 'var(--tf-text)' }}>
          What domestic-abuse survivors are entitled to
        </h2>
        <p
          className="font-body mt-2"
          style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
        >
          The Domestic Abuse Act 2021 and the Victims&apos; Code give you specific rights. Tap any
          right to read more.
        </p>
      </div>
      <div className="flex flex-col gap-3">
        {DA_RIGHTS.map((r) => (
          <Collapsible
            key={r.id}
            id={r.id}
            title={r.title}
            body={r.body}
            testid={`da-right-${r.id}`}
            icon={ShieldCheck}
          />
        ))}
      </div>

      {/* Specialist helplines */}
      <div className="mt-12 mb-4">
        <p className="eyebrow">SPECIALIST UK HELP</p>
        <h2 className="font-heading mt-2" style={{ fontSize: 22, color: 'var(--tf-text)' }}>
          Helplines &amp; services
        </h2>
        <p
          className="font-body mt-2"
          style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
        >
          All free. All confidential. You do not have to be ready to leave to call.
        </p>
      </div>
      <div className="flex flex-col gap-3">
        {DA_ORGS.map((o) => (
          <div key={o.id} className="tf-card p-5" data-testid={`da-org-${o.id}`}>
            {o.hours && (
              <div
                className="flex items-center gap-1"
                style={{ color: 'var(--tf-text-muted)', fontSize: 11 }}
              >
                <Clock size={11} />
                <span className="font-body">{o.hours}</span>
              </div>
            )}
            <h3
              className="font-heading mt-2"
              style={{ fontSize: 18, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
            >
              {o.name}
            </h3>
            <p
              className="font-body"
              style={{ fontSize: 12.5, color: 'var(--tf-purple)', fontWeight: 700, marginTop: 2 }}
            >
              {o.tagline}
            </p>
            <p
              className="font-body mt-3"
              style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
            >
              {o.description}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {o.phone && (
                <a
                  href={`tel:${o.phone}`}
                  data-testid={`da-call-${o.id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold"
                  style={{
                    background: 'var(--tf-purple)',
                    color: '#fff',
                    fontSize: 12.5,
                    textDecoration: 'none',
                  }}
                >
                  <Phone size={13} /> {o.phoneLabel}
                </a>
              )}
              {o.website && (
                <a
                  href={o.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`da-web-${o.id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold"
                  style={{
                    background: '#fff',
                    color: 'var(--tf-purple)',
                    fontSize: 12.5,
                    border: '1px solid var(--tf-purple-light)',
                    textDecoration: 'none',
                  }}
                >
                  <ExternalLink size={13} /> Visit website
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      </IfEnglandWales>

      {/* Scotland — parallel sections, same visual language as E&W */}
      <IfScotland>

      {/* Scope card */}
      <section
        className="mt-8 tf-card p-5 sm:p-6"
        data-testid="da-scotland-scope"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F4EFF8 100%)',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
              color: '#fff',
            }}
            aria-hidden="true"
          >
            <MapPin size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <p
              className="font-body font-bold"
              style={{ fontSize: 10.5, letterSpacing: '0.22em', color: 'var(--tf-purple-dark)' }}
            >
              A COMPLETELY SEPARATE LEGAL SYSTEM
            </p>
            <h2
              className="font-heading mt-1"
              style={{ fontSize: 'clamp(20px, 2.4vw, 23px)', color: 'var(--tf-text)', letterSpacing: '-0.01em', lineHeight: 1.25 }}
            >
              You are reading domestic abuse in Scots law.
            </h2>
            <p
              className="font-body mt-3"
              style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
            >
              Scotland has its own domestic-abuse statute — the{' '}
              <strong>Domestic Abuse (Scotland) Act 2018</strong> — and in some
              ways it goes <strong>further</strong> than E&amp;W by treating
              physical abuse and coercive control as a single offence. Every
              section below is Scots law.
            </p>
          </div>
        </div>
      </section>

      {/* Types of abuse in Scotland */}
      <section className="mt-12" data-testid="da-scot-types">
        <p className="eyebrow">SCOTS LAW · WHAT DOMESTIC ABUSE LOOKS LIKE</p>
        <h2 className="font-heading mt-2" style={{ fontSize: 22, color: 'var(--tf-text)' }}>
          The single offence of &ldquo;abusive behaviour&rdquo;.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          The 2018 Act rolls up physical violence, threats, coercive control,
          isolation, financial abuse, monitoring, humiliation and sexual
          coercion into <strong>one offence</strong> — &ldquo;engaging in an
          abusive course of behaviour&rdquo;. If a partner is doing any of the
          below, that alone is a crime in Scotland.
        </p>
        <ul className="mt-5 grid sm:grid-cols-2 gap-3" style={{ listStyle: 'none', padding: 0 }}>
          {[
            ['Physical violence or threats', 'Hitting, restraining, throwing things, or threats of violence against you, children or pets.'],
            ['Coercive control', 'Rules, monitoring your movements, controlling what you wear or eat, tracking your phone.'],
            ['Financial abuse', 'Controlling your money, running up debt in your name, sabotaging your job.'],
            ['Isolation', 'Cutting you off from friends, family, work, faith community or online groups.'],
            ['Gaslighting & humiliation', 'Making you doubt yourself, name-calling, ridicule in public or private.'],
            ['Sexual coercion', 'Pressuring or forcing sex or sexual acts — a crime whether inside or outside a relationship.'],
          ].map(([t, b]) => (
            <li key={t} className="tf-card p-4" style={{ borderColor: 'var(--tf-purple-light)' }}>
              <p className="font-body font-bold" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>{t}</p>
              <p className="font-body mt-1.5" style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>{b}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Legal protections in Scotland */}
      <section className="mt-12" data-testid="da-scot-legal">
        <p className="eyebrow">SCOTS LAW · YOUR LEGAL PROTECTIONS</p>
        <h2 className="font-heading mt-2" style={{ fontSize: 22, color: 'var(--tf-text)' }}>
          Orders, disclosure and specialist advocacy.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          Scotland has its own protective orders and disclosure scheme. Legal
          aid is normally available for civil protective orders.
        </p>
        <ul className="mt-5 flex flex-col gap-3" style={{ listStyle: 'none', padding: 0 }}>
          {[
            ['Domestic Abuse (Scotland) Act 2018', "Makes 'engaging in an abusive course of behaviour' a specific criminal offence — physical and psychological abuse together."],
            ['DAPNs & DAPOs (2021 Act)', 'Police Scotland can issue a Domestic Abuse Protection Notice on the spot; a Sheriff can grant a longer Domestic Abuse Protection Order — banning contact, controlling behaviour and use of the home.'],
            ['DSDAS (the Scottish Clare\u2019s Law)', 'The Disclosure Scheme for Domestic Abuse Scotland: Right to Ask (public) and Power to Tell (police).'],
            ['Non-harassment orders', 'Scottish courts can make one after conviction — breach is itself a criminal offence.'],
            ['MARAC (Scottish framework)', 'Multi-Agency Risk Assessment Conferences coordinated with Police Scotland Domestic Abuse Investigation Units.'],
            ['Scottish IDAA / ASSIST advocacy', "Independent Domestic Abuse Advocacy from Scottish Women's Aid local services and ASSIST — free, confidential, independent of police."],
          ].map(([t, b]) => (
            <li key={t} className="tf-card p-4" style={{ borderColor: 'var(--tf-purple-light)' }}>
              <p className="font-body font-bold" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>{t}</p>
              <p className="font-body mt-1.5" style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>{b}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Scotland-specialist helplines */}
      <section className="mt-12" data-testid="da-scot-helplines">
        <p className="eyebrow">SCOTS LAW · SCOTTISH SPECIALIST HELPLINES</p>
        <h2 className="font-heading mt-2" style={{ fontSize: 22, color: 'var(--tf-text)' }}>
          Free, confidential, understand Scots law.
        </h2>
        <ul className="mt-5 flex flex-col gap-3" style={{ listStyle: 'none', padding: 0 }}>
          {[
            ["Scotland's Domestic Abuse & Forced Marriage Helpline", '0800 027 1234 · 24 hours a day, every day', '08000271234'],
            ["Scottish Women's Aid", '0131 226 6606 · Mon–Fri office hours + local services', '01312266606'],
            ['Abused Men in Scotland (AMIS)', '0808 800 0024 · Mon–Fri office hours', '08088000024'],
            ['LGBT Helpline Scotland', '0300 123 2523 · see website for hours', '03001232523'],
          ].map(([name, sub, tel]) => (
            <li key={name} className="tf-card p-4 flex items-start gap-3" style={{ borderColor: 'var(--tf-purple-light)' }}>
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--tf-purple)', color: '#fff' }}
                aria-hidden="true"
              >
                <MapPin size={16} strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <p className="font-body font-bold" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>{name}</p>
                <a
                  href={`tel:${tel}`}
                  className="font-body"
                  style={{ fontSize: 13, color: 'var(--tf-purple-dark)', textDecoration: 'underline', fontWeight: 600 }}
                >
                  {sub}
                </a>
              </div>
            </li>
          ))}
        </ul>
      </section>

      </IfScotland>

      {/* Consent when you report — DA specific */}
      <section
        className="mt-12 tf-card p-5 sm:p-6"
        data-testid="da-consent-during-report"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #FBEFF2 100%)',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <p className="eyebrow">CONSENT DURING YOUR REPORT</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 22, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          You keep the right to say yes — or no — at every step.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          Reporting does not mean losing control. Ask for time. Ask for an IDVA. Ask why
          anything is being requested.
        </p>
        <ul className="mt-4 flex flex-col gap-2.5" style={{ listStyle: 'none', padding: 0 }}>
          <li className="tf-card p-3.5">
            <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
              An IDVA
            </p>
            <p className="font-body mt-1" style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              You choose whether to accept independent domestic-violence advocacy. It is
              free, confidential, and separate from the police.
            </p>
          </li>
          <li className="tf-card p-3.5">
            <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
              A DASH risk assessment &amp; MARAC referral
            </p>
            <p className="font-body mt-1" style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              For high-risk cases, agencies may share information at a MARAC (Multi-Agency
              Risk Assessment Conference). You can ask what is being shared and with whom.
            </p>
          </li>
          <li className="tf-card p-3.5">
            <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
              Third-party materials
            </p>
            <p className="font-body mt-1" style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              Medical notes, phone messages, children&apos;s social-care records — you can
              refuse and ask why any specific item is needed.
            </p>
          </li>
          <li className="tf-card p-3.5">
            <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
              Photographs, forensic evidence, medical records
            </p>
            <p className="font-body mt-1" style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              You choose whether photos are taken of any injuries, and whether medical
              records are shared with police.
            </p>
          </li>
          <li className="tf-card p-3.5">
            <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
              A Victim Personal Statement &amp; special measures
            </p>
            <p className="font-body mt-1" style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              You choose whether to write a VPS, and whether it is read out. Screens,
              video-link and a supporter can all be requested for court.
            </p>
          </li>
          <li className="tf-card p-3.5">
            <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
              Withdrawing from the process
            </p>
            <p className="font-body mt-1" style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              You can pause or withdraw. In some DA cases (especially where coercive
              control is charged) the police can proceed without you — your wishes should
              still be recorded and taken into account.
            </p>
          </li>
        </ul>
        <Link
          to="/app/consent"
          data-testid="da-to-consent"
          className="inline-flex items-center gap-1.5 mt-5"
          style={{ color: 'var(--tf-purple-dark)', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}
        >
          Read the full consent guide →
        </Link>
      </section>

      {/* Footer reassurance */}
      <div
        className="mt-12 p-5 rounded-2xl"
        style={{ background: 'var(--tf-pink-soft)' }}
      >
        <p
          className="font-body font-bold text-center"
          style={{ fontSize: 13, color: 'var(--tf-text)', marginBottom: 6 }}
        >
          You are not overreacting. You are not making it up. You are not to blame.
        </p>
        <p
          className="font-body text-center"
          style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
        >
          Abuse thrives on isolation and self-doubt. Reaching out to a specialist helpline —
          even once, anonymously — is a powerful first step. They will not pressure you to do
          anything you&apos;re not ready for.
        </p>
      </div>
    </div>
  );
}
